import { useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState, Fragment } from 'react';
import { useCookies } from 'react-cookie';
import { useDispatch, useSelector } from 'react-redux';
import JSZip from 'jszip';

import { getJobsAdmin } from 'store/actions/admin';
import { createRpc } from 'store/actions/rpc';
import { withTheme } from 'styled-components';
import { CloseOutlined, FovLoader, FovOption, FovSelect, SearchInputField, VerticalSpacer } from 'components';
import { FovModal } from 'components/generic';
import { UploadOutlined } from 'components/icons';
import { MemberContainer } from 'pages/ArchVisionAdminDashboard/styles';
import {
    TitleRow,
    UserFilterTag,
    FilterContainer,
    FilterGroup,
    FilterLabel,
    FilterSelectContainer,
    JobsHeaderContainer,
    JobTypeSelectContainer,
    StatusSelectContainer,
    RefreshButton,
    TableArea,
    TableFooter,
    ResultSummary,
    RowsPerPage,
    RowsPerPageSelect,
    EmptyJobsState,
    EmptyStateTitle,
    EmptyStateText,
    ClearFilterButton,
    UploadJobButton,
    UploadMenu,
    UploadMenuLabel,
    FileDropLabel,
    HiddenFileInput,
    UploadSubmitButton,
} from './styles';
import { DeleteTagButton } from 'components/tags/styles';
import { listColumns } from './listColumns';

const setDirectoryInput = node => {
    if (node) node.webkitdirectory = true;
};

const zipEntryPath = file => file.webkitRelativePath?.split('/').slice(1).join('/') || file.name;

const AdminJobs = withTheme(props => {
    const { theme } = props;
    const dispatch = useDispatch();

    const userDataStore = useSelector(state => state.userData);
    const adminDataStore = useSelector(state => state.adminData);
    const searchFieldRef = useRef('');

    const [ cookies ] = useCookies([ 'userAuth' ]);
    const token = cookies.archvision_token;

    const [ openMenuGuid, setOpenMenuGuid ] = useState(null);
    const [ uploadOpen, setUploadOpen ] = useState(false);
    const [ uploadFiles, setUploadFiles ] = useState([]);
    const [ uploadJobType, setUploadJobType ] = useState('');

    const uploadFolderName = uploadFiles[0]?.webkitRelativePath?.split('/')[0];

    const selectedTheme = userDataStore.userPreferences.theme;
    const jobs = useMemo(() => adminDataStore.jobs, [ adminDataStore.jobs ]);
    const jobTypes = useMemo(() => adminDataStore.jobTypes ?? [], [ adminDataStore.jobTypes ]);
    const jobStatuses = useMemo(() => adminDataStore.jobStatuses ?? [], [ adminDataStore.jobStatuses ]);
    const users = useMemo(() => adminDataStore.users, [ adminDataStore.users ]);
    const isLoading = useMemo(() => adminDataStore.isLoading, [ adminDataStore.isLoading ]);
    const pagination = useMemo(() => adminDataStore.pagination, [ adminDataStore.pagination ]);
    const { page, pageSize, totalCount } = pagination;

    const [ searchParams, setSearchParams ] = useSearchParams();

    const userIdFilter = searchParams.get('user_id');
    const filteredUser = useMemo(
        () => users?.find(u => String(u.id) === String(userIdFilter)),
        [ users, userIdFilter ],
    );
    const userFilterLabel = filteredUser?.email ?? `User ID: ${userIdFilter}`;

    const handlePageChange = page => {
        if (Number(searchParams.get('page')) === page) return;

        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set('page', page);
        setSearchParams(nextSearchParams);
    };

    const handleBlur = () => {
        const value = searchFieldRef.current.value;

        if (value === '') {
            searchParams.delete('search');
            searchParams.set('page', 1);
            setSearchParams(searchParams);
            return;
        }

        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set('search', value);
        nextSearchParams.set('page', 1);
        setSearchParams(nextSearchParams);
    };

    const handleFilterChange = (parameter, value) => {
        const nextSearchParams = new URLSearchParams(searchParams);

        if (value === '') {
            nextSearchParams.delete(parameter);
        } else {
            nextSearchParams.set(parameter, value);
        }

        nextSearchParams.set('page', 1);
        setSearchParams(nextSearchParams);
    };

    const handleClearFilters = () => {
        const nextSearchParams = new URLSearchParams(searchParams);

        nextSearchParams.delete('search');
        nextSearchParams.delete('status');
        nextSearchParams.delete('job_type');
        nextSearchParams.delete('user_id');
        nextSearchParams.set('page', 1);

        if (searchFieldRef.current) {
            searchFieldRef.current.value = '';
        }

        setSearchParams(nextSearchParams);
    };

    const hasActiveFilters = [
        'search',
        'status',
        'job_type',
        'user_id',
    ].some(parameter => searchParams.has(parameter));

    const handlePageSizeChange = value => {
        const nextSearchParams = new URLSearchParams(searchParams);

        nextSearchParams.set('per_page', value);
        nextSearchParams.set('page', 1);

        setSearchParams(nextSearchParams);
    };

    const handleUploadClose = () => {
        setUploadOpen(false);
        setUploadFiles([]);
        setUploadJobType('');
    };

    const handleUploadJob = () => {
        const { userId, email: userEmail } = userDataStore.data;
        const zip = new JSZip();

        uploadFiles.forEach(file => zip.file(zipEntryPath(file), file));

        dispatch(createRpc({
            token,
            userId,
            userEmail,
            payload: { job_type: uploadJobType, zip },
            onSuccess: () => dispatch(getJobsAdmin(token)),
        }));

        handleUploadClose();
    };

    const queryString = searchParams.toString();
    useEffect(() => {
        if (!token) return;

        dispatch(getJobsAdmin(token));
    }, [ dispatch, token, queryString ]);

    const createdOption = [
        { value: 'CREATED_NEW_TO_OLD', label: 'Newest first' },
        { value: 'CREATED_OLD_TO_NEW', label: 'Oldest first' },
    ];

    return (
        <Fragment>
            {userIdFilter && (
                <TitleRow>
                    <UserFilterTag theme={theme} $selectedTheme={selectedTheme}>
                        {userFilterLabel}
                        <DeleteTagButton onClick={() => {
                            const nextSearchParams = new URLSearchParams(searchParams);
                            nextSearchParams.delete('user_id');
                            nextSearchParams.set('page', 1);
                            setSearchParams(nextSearchParams);
                            dispatch(getJobsAdmin(token));
                        }}>
                            <CloseOutlined width={14} height={14} />
                        </DeleteTagButton>
                    </UserFilterTag>
                </TitleRow>
                )}
            <VerticalSpacer size={7} />
            <FovLoader
                text='Retrieving Jobs'
                resource={!isLoading}
            >
                <JobsHeaderContainer>
                    <SearchInputField
                        width={300}
                        disabled={false}
                        ref={searchFieldRef}
                        defaultValue={searchFieldRef.current?.value || searchParams.get('search')}
                        onChange={e => searchFieldRef.current.value = e.target.value}
                        onKeyUp={e => e.key === 'Enter' && e.target.blur()}
                        onBlur={handleBlur}
                    />
                    <FilterContainer>
                        <UploadJobButton
                            $selectedTheme={selectedTheme}
                            type='button'
                            onClick={() => setUploadOpen(true)}
                        >
                            <UploadOutlined width={16} height={16} />
                            Upload Job
                        </UploadJobButton>
                        <FilterGroup>
                            <FilterLabel $selectedTheme={selectedTheme}>Status</FilterLabel>
                            <StatusSelectContainer>
                                <FovSelect
                                    value={searchParams.get('status') || ''}
                                    onChange={(event, newValue) => {
                                        handleFilterChange('status', newValue);
                                    }}
                                >
                                    <FovOption value=''>All</FovOption>

                                    {jobStatuses.map(item => (
                                        <FovOption
                                            key={item.status}
                                            value={item.status}
                                        >
                                            {item.status
                                                ?.replaceAll('_', ' ')
                                                .replace(/\b\w/g, letter => letter.toUpperCase())}
                                        </FovOption>
                                    ))}
                                </FovSelect>
                            </StatusSelectContainer>
                        </FilterGroup>
                        <FilterGroup>
                            <FilterLabel $selectedTheme={selectedTheme}>Job Type</FilterLabel>
                            <JobTypeSelectContainer>
                                <FovSelect
                                    value={searchParams.get('job_type') || ''}
                                    onChange={(event, newValue) => {
                                        handleFilterChange('job_type', newValue);
                                    }}
                                >
                                    <FovOption value=''>All</FovOption>

                                    {jobTypes.map(item => (
                                        <FovOption key={item.type} value={item.type}>
                                            {item.type}
                                        </FovOption>
                                    ))}
                                </FovSelect>
                            </JobTypeSelectContainer>
                        </FilterGroup>
                        <FilterGroup>
                            <FilterLabel $selectedTheme={selectedTheme}>Created</FilterLabel>
                            <FilterSelectContainer>
                                <FovSelect
                                    value={searchParams.get('order_by') || 'CREATED_NEW_TO_OLD'}
                                    onChange={(event, newValue) => {
                                        handleFilterChange('order_by', newValue);
                                    }}
                                >
                                    {createdOption.map(option => (
                                        <FovOption
                                            key={option.value}
                                            value={option.value}
                                        >{option.label}</FovOption>
                                    ))}
                                </FovSelect>
                            </FilterSelectContainer>
                        </FilterGroup>
                        <RefreshButton
                            $selectedTheme={selectedTheme}
                            type='button'
                            disabled={isLoading}
                            onClick={() => dispatch(getJobsAdmin(token))}
                        >
                            <span aria-hidden='true'>↻</span>
                            {isLoading ? 'Refreshing...' : 'Refresh'}
                        </RefreshButton>
                    </FilterContainer>
                </JobsHeaderContainer>

                <FovModal
                    visible={uploadOpen}
                    title='Upload Job'
                    maxWidth={480}
                    onClose={handleUploadClose}
                >
                    <UploadMenu>
                        <div>
                            <UploadMenuLabel $selectedTheme={selectedTheme}>
                                Job package
                            </UploadMenuLabel>
                            <FileDropLabel $selectedTheme={selectedTheme}>
                                {uploadFolderName
                                    ? `${uploadFolderName} (${uploadFiles.length} files)`
                                    : 'Click to select a job folder'}
                                <HiddenFileInput
                                    multiple
                                    type='file'
                                    ref={setDirectoryInput}
                                    onChange={event => setUploadFiles(Array.from(event.target.files ?? []))}
                                />
                            </FileDropLabel>
                        </div>

                        <div>
                            <UploadMenuLabel $selectedTheme={selectedTheme}>
                                Job type
                            </UploadMenuLabel>
                            <FovSelect
                                value={uploadJobType}
                                onChange={(event, newValue) => setUploadJobType(newValue)}
                            >
                                <FovOption value=''>Select a job type</FovOption>

                                {jobTypes.map(item => (
                                    <FovOption key={item.type} value={item.type}>
                                        {item.type}
                                    </FovOption>
                                ))}
                            </FovSelect>
                        </div>

                        <UploadSubmitButton
                            $selectedTheme={selectedTheme}
                            type='button'
                            disabled={!uploadFiles.length || !uploadJobType}
                            onClick={handleUploadJob}
                        >
                            Upload Job
                        </UploadSubmitButton>
                    </UploadMenu>
                </FovModal>

                {jobs?.length === 0 ? (
                    <EmptyJobsState $selectedTheme={selectedTheme}>
                        {hasActiveFilters ? (
                            <>
                                <EmptyStateTitle>No Matching Jobs</EmptyStateTitle>
                                <EmptyStateText>
                                    Try changing your search or clearing the filters.
                                </EmptyStateText>

                                <ClearFilterButton
                                    $selectedTheme={selectedTheme}
                                    type='button'
                                    onClick={handleClearFilters}
                                >Clear Filters</ClearFilterButton>
                            </>
                        ) : (
                            <>
                                <EmptyStateTitle>No Jobs Available</EmptyStateTitle>
                                <EmptyStateText>
                                    New jobs will appear here when they are created.
                                </EmptyStateText>
                            </>
                        )}
                    </EmptyJobsState>
                ) : (
                    <Fragment>
                        <TableArea>
                            <MemberContainer
                                dataSource={jobs}
                                $selectedTheme={selectedTheme}
                                columns={listColumns(selectedTheme, openMenuGuid, setOpenMenuGuid)}
                                rowKey={'row-' + Math.random()}
                                currentPage={page}
                                pageSize={pageSize}
                                totalCount={totalCount}
                                isPagination={true}
                                selectedKey='job_guid'
                                selectedGuid={null}
                                onPageChange={handlePageChange}
                            />
                        </TableArea>
                        <TableFooter $selectedTheme={selectedTheme}>
                            <ResultSummary $selectedTheme={selectedTheme}>
                                { totalCount === 0
                                    ? 'Showing 0 jobs'
                                    : `Showing ${(page - 1) * pageSize + 1} to ${
                                        Math.min(page * pageSize, totalCount)
                                    } of ${totalCount} jobs` }
                            </ResultSummary>
                            <RowsPerPage>
                                <FilterLabel $selectedTheme={selectedTheme}>
                                    Rows per page:
                                </FilterLabel>
                                <RowsPerPageSelect>
                                    <FovSelect
                                        value={String(
                                            searchParams.get('per_page') || pageSize || 10,
                                        )}
                                        onChange={(event, newValue) => {
                                            handlePageSizeChange(newValue);
                                        }}
                                    >
                                        <FovOption value='10'>10</FovOption>
                                        <FovOption value='25'>25</FovOption>
                                        <FovOption value='50'>50</FovOption>
                                        <FovOption value='100'>100</FovOption>
                                    </FovSelect>
                                </RowsPerPageSelect>
                            </RowsPerPage>
                        </TableFooter>
                    </Fragment>
                )}
            </FovLoader>
        </Fragment>
    );
});

export default AdminJobs;